// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DeliveryInventoryMessage from "@bg/App/Components/OnePageCheckout/DeliveryInventoryMessage";
import xSaveShipping from "@bg/App/Handlers/OnePageCheckout/XngSaveShipping";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { CHECKOUT_SHIPPING_FORM_ID } from "@bg/Overrides/Widgets/OnePageCheckout/OnePageCheckoutShippingSection";
import ShippingAddressForm from "@bg/Overrides/Widgets/OnePageCheckout/ShippingAddressForm";
import { doScroll, xClassNames } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import OnePageCheckoutState from "@insite/client-framework/Store/Pages/OnePageCheckout/OnePageCheckoutState";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { CountryModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { OnePageCheckoutFormContext } from "@insite/content-library/Pages/OnePageCheckoutPage";
import { ShippingAddressFormStyles } from "@insite/content-library/Widgets/OnePageCheckout/ShippingAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

interface OwnProps {
    cart?: Cart;
    countries: CountryModel[];
    shippingAddressFormStateSource: OnePageCheckoutState["shippingAddressFormState"];
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        shippingAddressFormState: state.pages.onePageCheckout.shippingAddressFormState,
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        isShippingAddressUpdateRequired: state.pages.onePageCheckout.isShippingAddressUpdateRequired,
        isShippingAddressEditing: state.xOnePageCheckout.isShippingAddressEditing,
        isBillingAddressEditing: state.xOnePageCheckout.isBillingAddressEditing,
        useOneTimeAddress: state.xOnePageCheckout.useOneTimeAddress,
        useBillingAddress: state.xOnePageCheckout.useBillingAddress,
        isBillingAddressUpdateRequired: state.pages.onePageCheckout.isBillingAddressUpdateRequired,
        isBillingAddressTheSameAsShipping: state.pages.onePageCheckout.isBillingAddressTheSameAsShipping,
    };
};

const mapDispatchToProps = {
    xSaveShipping,
    setAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/OnePageCheckout/SetShippingAddress",
            address,
        });
    },
    setIsShippingAddressEditing: (isEditing: boolean) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xOnePageCheckout/IsShippingAddressEditing" as any,
            isEditing,
        });
    },
    setIsSubmitting: (isSubmitting: boolean) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xOnePageCheckout/IsSubmitting" as any,
            isSubmitting,
        });
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface CheckoutShippingAddressFormStyles {
    container?: InjectableCss;
    shippingAddressForm?: ShippingAddressFormStyles;
    actionContainer?: InjectableCss;
    saveAddressButton?: ButtonPresentationProps;
    cancelEditButton?: ButtonPresentationProps;
}

export const checkoutShippingAddressFormStyles: CheckoutShippingAddressFormStyles = {};

const styles = checkoutShippingAddressFormStyles;
const CheckoutShippingAddressForm: FC<Props> = ({
    cart,
    shippingAddressFormState,
    shipToAddressFields,
    isShippingAddressEditing,
    isShippingAddressUpdateRequired,
    countries,
    useBillingAddress,
    isBillingAddressTheSameAsShipping,
    isBillingAddressUpdateRequired,
    isBillingAddressEditing,
    shippingAddressFormStateSource,
    setAddress,
    setIsShippingAddressEditing,
    setIsSubmitting,
    xSaveShipping,
}) => {
    const context = useContext(OnePageCheckoutFormContext);

    /**
     * XNG-Changes
     * Reset BillingAddressFormState when click on Cancel
     */
    const oneTimeEditAddressClose = () => {
        if (!shippingAddressFormStateSource?.address) {
            return;
        }

        setAddress({
            ...shippingAddressFormStateSource?.address,
        });
        setIsShippingAddressEditing(false);
    };

    /**
     * XNG-Changes
     * Saved All Addresses
     */
    const onSaveNewAddressClickHandler = async () => {
        context.formSubmitAttempted = true;
        setIsSubmitting(true);

        const address = shippingAddressFormState?.address;
        if (!address) {
            return;
        }

        const validators: (() => Promise<boolean>)[] = [];
        Object.keys(context.validators).forEach(key => {
            const validator = context.validators[key];
            if (key !== "shippingAddress") {
                return;
            }

            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));
        if (results.every(o => o)) {
            xSaveShipping({
                onSuccess: () => {
                    context.formSubmitAttempted = false;
                    setIsShippingAddressEditing(false);
                },
            });
        } else {
            context.formSubmitAttempted = false;
            doScroll();
        }
    };

    if (!shipToAddressFields || (!isShippingAddressUpdateRequired && !isShippingAddressEditing)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="OnePageCheckoutShippingAddressForm">
            <ShippingAddressForm
                countries={countries}
                fieldDisplay={shipToAddressFields}
                formId={CHECKOUT_SHIPPING_FORM_ID}
                disabled={isBillingAddressTheSameAsShipping && useBillingAddress}
                extendedStyles={styles.shippingAddressForm}
            />
            <DeliveryInventoryMessage cart={cart} />
            <StyledWrapper {...styles.actionContainer}>
                {!(isBillingAddressUpdateRequired || isBillingAddressEditing) && (
                    <Button
                        onClick={onSaveNewAddressClickHandler}
                        type="button"
                        {...styles.saveAddressButton}
                        className={xClassNames.append(isShippingAddressEditing && "full-width")}
                    >
                        {translate(useBillingAddress ? "Save Address" : "Set One-Time Address")}
                    </Button>
                )}
                {isShippingAddressEditing && (
                    <Button onClick={oneTimeEditAddressClose} {...styles.cancelEditButton}>
                        {translate("Cancel")}
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutShippingAddressForm);
